import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  Wrench, Plus, Pencil, Trash2, X, Save, Loader2, ArrowLeft, RefreshCw, Search, Receipt,
  CheckCircle, Ban, RotateCcw, History, Settings, BarChart3, AlertTriangle, Car, ClipboardList, Bell,
} from "lucide-react";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10 disabled:opacity-50";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";
const SELECT = INPUT;

const ESTADO_LABEL = { abierta: "Abierta", en_proceso: "En proceso", cerrada: "Cerrada", facturada: "Facturada", anulada: "Anulada" };
const ESTADO_COLOR = {
  abierta: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  en_proceso: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  cerrada: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  facturada: "text-violet-400 bg-violet-400/10 border-violet-400/20",
  anulada: "text-slate-400 bg-slate-400/10 border-slate-400/20",
};
const TIPO_LABEL = { interna: "Uso interno", cliente: "Cliente" };
const fmt = (n) => (n === null || n === undefined ? "—" : `₲ ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`);
const num = (n) => (n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString("es-PY", { maximumFractionDigits: 2 }));

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
    <div className={`bg-arandu-dark border border-white/10 rounded-2xl w-full ${wide ? "max-w-5xl" : "max-w-xl"} my-8`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

// ── Buscador simple (cliente) ─────────────────────────────────────────────────
function EntidadSelect({ entidades, value, onSelect, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const l = (entidades || []).filter(e => !q || (e.nombre || "").toLowerCase().includes(q) || (e.razon_social || "").toLowerCase().includes(q) || (e.ruc || "").toLowerCase().includes(q));
    return l.slice(0, 12);
  }, [entidades, query]);
  const sel = (entidades || []).find(e => e.id === value);
  if (sel) {
    return (
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
        <span className="text-sm text-white flex-1 truncate">{sel.razon_social || sel.nombre}</span>
        <button type="button" onClick={() => onSelect(null)} className="text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <div className="relative" ref={ref}>
      <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input className={`${INPUT} pl-8`} placeholder={placeholder} value={query} onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-56 overflow-y-auto">
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Sin resultados</div>}
          {filtered.map(e => (
            <button type="button" key={e.id} onClick={() => { onSelect(e.id); setQuery(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white">
              {e.razon_social || e.nombre}{e.ruc ? ` · ${e.ruc}` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Buscador de repuestos del catálogo ────────────────────────────────────────
function RepuestoPicker({ token, slug, onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/admin/productos?search=${encodeURIComponent(q.trim())}&page=1&page_size=15&activo=true&logo_tipo=${encodeURIComponent(slug)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) { const d = await r.json(); setRes(d.productos || []); setOpen(true); }
      } catch { /* silencioso */ }
    }, 300);
    return () => clearTimeout(t);
  }, [q, slug, token]);
  const opciones = [];
  res.forEach(p => {
    if (p.variantes_habilitadas && (p.variantes || []).length) {
      p.variantes.filter(v => v.activo !== false).forEach(v => opciones.push({ p, v }));
    } else opciones.push({ p, v: null });
  });
  return (
    <div className="relative" ref={ref}>
      <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input className={`${INPUT} pl-8`} placeholder="Buscar repuesto del catálogo (nombre, SKU)…" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res.length && setOpen(true)} />
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {opciones.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Sin resultados</div>}
          {opciones.map(({ p, v }) => {
            const base = v || p;
            const stock = Number(base.stock_actual || 0);
            return (
              <button type="button" key={`${p.id}-${v?.id || ""}`}
                onClick={() => { onPick(p, v); setQ(""); setRes([]); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white flex justify-between gap-3">
                <span className="truncate">{p.nombre}{v ? ` — ${v.atributos_texto || v.nombre || ""}` : ""}{(base.sku || p.sku) ? ` · ${base.sku || p.sku}` : ""}</span>
                <span className={`text-xs shrink-0 ${stock > 0 ? "text-emerald-400" : "text-red-400"}`}>stock {num(stock)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const itemVacio = (tipo) => ({ tipo, producto_id: "", variante_id: "", descripcion: "", sku: "", cantidad: 1, costo_unitario: 0, precio_unitario: 0, iva_tipo: "10", tercero: "" });

export default function TallerPage() {
  const navigate = useNavigate();
  const { token, hasPermission, activeEmpresaPropia, user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const jsonH = { ...headers, "Content-Type": "application/json" };
  const slug = activeEmpresaPropia?.slug || "";
  const qs = slug ? `?logo_tipo=${encodeURIComponent(slug)}` : "";
  const qsAnd = slug ? `&logo_tipo=${encodeURIComponent(slug)}` : "";

  const isAdmin = ["admin", "super_admin", "gerente"].includes(user?.role);
  const can = (a) => isAdmin || hasPermission(`taller.${a}`);
  const canVer = can("ver");

  const [tab, setTab] = useState("ordenes");
  const [cfg, setCfg] = useState(null);
  const [ordenes, setOrdenes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [search, setSearch] = useState("");
  const [seleccion, setSeleccion] = useState([]);
  const [ordenModal, setOrdenModal] = useState(null);   // {orden|null, tipo}
  const [historial, setHistorial] = useState(null);
  const [anular, setAnular] = useState(null);

  const tipos = cfg?.tipos_permitidos || [];

  const fetchAll = useCallback(async () => {
    if (!canVer) { setLoading(false); return; }
    try {
      const rc = await fetch(`${API}/admin/taller/config${qs}`, { headers });
      const c = rc.ok ? await rc.json() : null;
      setCfg(c);
      const [rO, rCc, rCl, rA] = await Promise.all([
        fetch(`${API}/admin/taller/ordenes${qs}`, { headers }),
        fetch(`${API}/admin/taller/centros-costo${qs}`, { headers }),
        fetch(`${API}/admin/entidades?es_cliente=true${qsAnd}`, { headers }),
        fetch(`${API}/admin/taller/alertas${qs}`, { headers }),
      ]);
      if (rO.ok) setOrdenes(await rO.json());
      if (rCc.ok) setCentros(await rCc.json());
      if (rCl.ok) { const d = await rCl.json(); setClientes(Array.isArray(d) ? d : (d.entidades || [])); }
      if (rA.ok) setAlertas(await rA.json());
    } catch { toast.error("Error al cargar Taller"); }
    setLoading(false);
    setRefreshing(false);
  }, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const lista = useMemo(() => ordenes.filter(o =>
    (!filtroTipo || o.tipo === filtroTipo) && (!filtroEstado || o.estado === filtroEstado) &&
    (!search.trim() || `${o.numero} ${o.activo_nombre} ${o.cliente_nombre || ""} ${o.descripcion || ""} ${o.mecanico || ""}`.toLowerCase().includes(search.toLowerCase()))
  ), [ordenes, filtroTipo, filtroEstado, search]);

  const cambiarEstado = async (o, estado, motivo) => {
    const r = await fetch(`${API}/admin/taller/ordenes/${o.id}/estado`, { method: "PATCH", headers: jsonH, body: JSON.stringify({ estado, motivo }) });
    if (r.ok) { toast.success(estado === "cerrada" ? "OT cerrada" : estado === "anulada" ? "OT anulada" : "OT actualizada"); fetchAll(); return true; }
    const e = await r.json().catch(() => ({}));
    toast.error(e.detail || "No se pudo cambiar el estado");
    return false;
  };

  const eliminar = async (o) => {
    if (!window.confirm(`¿Eliminar la ${o.numero}?`)) return;
    const r = await fetch(`${API}/admin/taller/ordenes/${o.id}`, { method: "DELETE", headers });
    if (r.ok) { toast.success("OT eliminada"); fetchAll(); } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "No se pudo eliminar"); }
  };

  const abrirOrden = async (o, tipo) => {
    if (o) {
      const r = await fetch(`${API}/admin/taller/ordenes/${o.id}`, { headers });
      if (r.ok) setOrdenModal({ orden: await r.json(), tipo: o.tipo });
    } else setOrdenModal({ orden: null, tipo });
  };

  const seleccionables = lista.filter(o => o.tipo === "cliente" && o.estado === "cerrada");
  const selActuales = seleccionables.filter(o => seleccion.includes(o.id));
  const facturar = () => {
    const clis = new Set(selActuales.map(o => o.cliente_id));
    if (!selActuales.length) return;
    if (clis.size > 1) { toast.error("Elegí OT del mismo cliente para facturar juntas"); return; }
    navigate(`/sistema/ventas?nuevo=factura_taller&ot_ids=${selActuales.map(o => o.id).join(",")}`);
  };

  if (!canVer) return <div className="min-h-screen bg-arandu-dark text-white p-8">Sin permiso para ver Taller.</div>;

  const tabs = [
    { key: "ordenes", label: "Órdenes de trabajo", icon: <ClipboardList className="w-4 h-4" /> },
    { key: "bienes", label: "Bienes / Historial", icon: <Car className="w-4 h-4" /> },
    { key: "preventivo", label: `Preventivo${alertas.length ? ` (${alertas.length})` : ""}`, icon: <Bell className="w-4 h-4" /> },
    { key: "reportes", label: "Reportes", icon: <BarChart3 className="w-4 h-4" /> },
    ...(can("configurar") ? [{ key: "config", label: "Configuración", icon: <Settings className="w-4 h-4" /> }] : []),
  ];

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-1 -ml-1 rounded-lg hover:bg-white/5"><ArrowLeft className="w-5 h-5" /></button>
          <Wrench className="w-6 h-6 text-orange-400" />
          <div>
            <h1 className="text-lg font-bold">Taller</h1>
            <p className="text-xs text-slate-400">{activeEmpresaPropia?.nombre || ""} — Órdenes de trabajo</p>
          </div>
        </div>
        <button onClick={() => { setRefreshing(true); fetchAll(); }} disabled={refreshing} className={BTN_SEC}><RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /></button>
      </div>

      <div className="flex gap-1 px-6 pt-4 border-b border-white/10 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? "text-white border-blue-500 bg-blue-500/10" : "text-slate-400 border-transparent hover:text-white hover:border-white/20"}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}

        {!loading && tipos.length === 0 && tab !== "config" && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-sm text-amber-300">
            No hay ningún tipo de OT habilitado para tu usuario en esta empresa. Revisá la Configuración del módulo y tus permisos (Ver OT de uso interno / de clientes).
          </div>
        )}

        {!loading && tab === "ordenes" && tipos.length > 0 && (
          <div>
            {alertas.length > 0 && (
              <button onClick={() => setTab("preventivo")} className="w-full mb-4 flex items-center gap-2 text-left bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-red-300 hover:bg-red-500/15">
                <AlertTriangle className="w-4 h-4" /> {alertas.filter(a => a.estado_alerta === "vencida").length} mantenimiento(s) vencido(s) y {alertas.filter(a => a.estado_alerta === "proxima").length} próximo(s). Ver preventivo.
              </button>
            )}
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input className={`${INPUT} pl-8 w-56`} placeholder="Buscar OT, bien, cliente…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                {tipos.length > 1 && (
                  <select className={`${SELECT} w-40`} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ colorScheme: "dark" }}>
                    <option value="">Todos los tipos</option>
                    {tipos.map(t => <option key={t} value={t}>{t === "cliente" ? "Para clientes" : "Uso interno"}</option>)}
                  </select>
                )}
                <select className={`${SELECT} w-40`} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ colorScheme: "dark" }}>
                  <option value="">Todos los estados</option>
                  {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {selActuales.length > 0 && can("facturar") && (isAdmin || hasPermission("facturas.crear")) && (
                  <button onClick={facturar} className={BTN_SEC}><Receipt className="w-4 h-4 text-violet-400" /> Facturar {selActuales.length} OT</button>
                )}
              </div>
              {can("crear") && (
                <div className="flex gap-2">
                  {tipos.includes("interna") && <button onClick={() => abrirOrden(null, "interna")} className={BTN_PRI}><Plus className="w-4 h-4" /> OT uso interno</button>}
                  {tipos.includes("cliente") && <button onClick={() => abrirOrden(null, "cliente")} className={BTN_PRI}><Plus className="w-4 h-4" /> OT para cliente</button>}
                </div>
              )}
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">N°</th><th className="text-left px-3 py-2">Fecha</th>
                    <th className="text-left px-3 py-2">Tipo</th><th className="text-left px-3 py-2">Bien</th>
                    <th className="text-left px-3 py-2">Cliente / Centro de costo</th>
                    <th className="text-right px-3 py-2">Km</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-left px-3 py-2">Estado</th><th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500">Sin órdenes de trabajo</td></tr>}
                  {lista.map(o => (
                    <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                      <td className="px-3 py-2">
                        {o.tipo === "cliente" && o.estado === "cerrada" && (
                          <input type="checkbox" checked={seleccion.includes(o.id)} onChange={() => setSeleccion(s => s.includes(o.id) ? s.filter(x => x !== o.id) : [...s, o.id])} />
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{o.numero}</td>
                      <td className="px-3 py-2 text-slate-300">{o.fecha}</td>
                      <td className="px-3 py-2 text-slate-300">{TIPO_LABEL[o.tipo]}</td>
                      <td className="px-3 py-2">{o.activo_nombre}</td>
                      <td className="px-3 py-2 text-slate-300">{o.tipo === "cliente" ? o.cliente_nombre : (o.centro_costo_nombre || "—")}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{num(o.km)}</td>
                      <td className="px-3 py-2 text-right">{fmt(o.tipo === "cliente" ? o.total_venta : o.total_costo)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_COLOR[o.estado]}`}>{ESTADO_LABEL[o.estado]}</span>
                        {o.factura_numero && <div className="text-[10px] text-violet-300 mt-0.5">Fact. {o.factura_numero}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-end">
                          <button title={["abierta", "en_proceso"].includes(o.estado) && can("editar") ? "Editar" : "Ver"} onClick={() => abrirOrden(o)} className="p-1.5 text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
                          {o.estado === "abierta" && can("editar") && <button title="Pasar a En proceso" onClick={() => cambiarEstado(o, "en_proceso")} className="p-1.5 text-blue-400 hover:text-blue-300"><Wrench className="w-4 h-4" /></button>}
                          {["abierta", "en_proceso"].includes(o.estado) && can("cerrar") && <button title="Cerrar OT" onClick={() => { if (window.confirm(`¿Cerrar la ${o.numero}?`)) cambiarEstado(o, "cerrada"); }} className="p-1.5 text-emerald-400 hover:text-emerald-300"><CheckCircle className="w-4 h-4" /></button>}
                          {o.estado === "cerrada" && can("cerrar") && <button title="Reabrir" onClick={() => cambiarEstado(o, "en_proceso")} className="p-1.5 text-amber-400 hover:text-amber-300"><RotateCcw className="w-4 h-4" /></button>}
                          {["abierta", "en_proceso", "cerrada"].includes(o.estado) && can("anular") && <button title="Anular" onClick={() => setAnular({ o, motivo: "" })} className="p-1.5 text-red-400 hover:text-red-300"><Ban className="w-4 h-4" /></button>}
                          {["abierta", "anulada"].includes(o.estado) && can("eliminar") && <button title="Eliminar" onClick={() => eliminar(o)} className="p-1.5 text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && tab === "bienes" && <BienesTab {...{ API, headers, qs, slug, setHistorial }} />}
        {!loading && tab === "preventivo" && <PreventivoTab {...{ API, headers, jsonH, qs, can, alertas, fetchAll }} />}
        {!loading && tab === "reportes" && <ReportesTab {...{ API, headers, qs, tipos }} />}
        {!loading && tab === "config" && can("configurar") && <ConfigTab {...{ API, headers, jsonH, qs, slug, cfg, centros, onSaved: fetchAll }} />}
      </div>

      {ordenModal && (
        <OrdenModal {...{ API, headers, jsonH, qs, slug, token, can, isAdmin, clientes, centros, cfg }}
          orden={ordenModal.orden} tipo={ordenModal.tipo}
          onClose={() => setOrdenModal(null)} onSaved={() => { setOrdenModal(null); fetchAll(); }} />
      )}
      {historial && <HistorialModal data={historial} onClose={() => setHistorial(null)} />}
      {anular && (
        <Modal title={`Anular ${anular.o.numero}`} onClose={() => setAnular(null)}>
          <p className="text-sm text-slate-400 mb-3">Se devuelve al stock lo que la OT haya descontado.</p>
          <label className={LABEL}>Motivo (obligatorio)</label>
          <textarea className={INPUT} rows={3} value={anular.motivo} onChange={e => setAnular(a => ({ ...a, motivo: e.target.value }))} />
          <div className="flex justify-end gap-2 mt-4">
            <button className={BTN_SEC} onClick={() => setAnular(null)}>Cancelar</button>
            <button className={BTN_PRI} disabled={!anular.motivo.trim()} onClick={async () => { if (await cambiarEstado(anular.o, "anulada", anular.motivo)) setAnular(null); }}>Anular OT</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Modal Orden de trabajo ────────────────────────────────────────────────────
function OrdenModal({ API, headers, jsonH, qs, slug, token, can, isAdmin, clientes, centros, cfg, orden, tipo, onClose, onSaved }) {
  const editable = !orden || ["abierta", "en_proceso"].includes(orden.estado);
  const esCliente = tipo === "cliente";
  const verCostos = isAdmin || can("ver_costos");
  const hoy = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState(() => ({
    fecha: orden?.fecha || hoy, activo_id: orden?.activo_id || "", km: orden?.km ?? "", horometro: orden?.horometro ?? "",
    cliente_id: orden?.cliente_id || "", centro_costo_id: orden?.centro_costo_id || "", mecanico: orden?.mecanico || "",
    descripcion: orden?.descripcion || "", observaciones: orden?.observaciones || "", deposito_id: orden?.deposito_id || "",
    reglas_cumplidas: orden?.reglas_cumplidas || [],
  }));
  const [items, setItems] = useState(orden?.items?.map(i => ({ ...i, tercero: i.tercero || "" })) || []);
  const [bienes, setBienes] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const extra = esCliente && f.cliente_id ? `&cliente_id=${encodeURIComponent(f.cliente_id)}` : "";
    if (esCliente && !f.cliente_id) { setBienes([]); return; }
    fetch(`${API}/admin/taller/bienes${qs}${extra}`, { headers }).then(r => r.ok ? r.json() : []).then(setBienes).catch(() => setBienes([]));
  }, [esCliente, f.cliente_id]); // eslint-disable-line
  useEffect(() => {
    if (!f.activo_id) { setReglas([]); return; }
    fetch(`${API}/admin/taller/reglas${qs}&activo_id=${f.activo_id}`, { headers }).then(r => r.ok ? r.json() : []).then(setReglas).catch(() => setReglas([]));
  }, [f.activo_id]); // eslint-disable-line

  const bien = bienes.find(b => b.id === f.activo_id);
  const totalCosto = items.reduce((s, i) => s + (Number(i.cantidad) || 0) * (Number(i.costo_unitario) || 0), 0);
  const totalVenta = items.reduce((s, i) => s + (Number(i.cantidad) || 0) * (Number(i.precio_unitario) || 0), 0);
  const setItem = (idx, patch) => setItems(l => l.map((x, i) => i === idx ? { ...x, ...patch } : x));

  const addRepuesto = (p, v) => {
    const base = v || p;
    setItems(l => [...l, {
      ...itemVacio("repuesto"), producto_id: p.id, variante_id: v?.id || "",
      descripcion: `${p.nombre}${v ? ` — ${v.atributos_texto || v.nombre || ""}` : ""}`,
      sku: base.sku || p.sku || "", costo_unitario: Number(base.precio_costo ?? p.precio_costo ?? 0),
      precio_unitario: Number(base.precio_venta ?? p.precio_venta ?? 0), iva_tipo: p.iva_tipo || "10",
    }]);
  };

  const guardar = async () => {
    if (!f.activo_id) { toast.error("Elegí el bien"); return; }
    if (esCliente && !f.cliente_id) { toast.error("Elegí el cliente"); return; }
    const body = {
      tipo, ...f, km: f.km === "" ? null : Number(f.km), horometro: f.horometro === "" ? null : Number(f.horometro),
      cliente_id: esCliente ? f.cliente_id : null, centro_costo_id: !esCliente && f.centro_costo_id ? f.centro_costo_id : null,
      deposito_id: f.deposito_id || null,
      items: items.map(i => ({
        id: i.id, tipo: i.tipo, producto_id: i.producto_id || null, variante_id: i.variante_id || null, descripcion: i.descripcion, sku: i.sku,
        cantidad: Number(i.cantidad) || 0, costo_unitario: i.costo_unitario === "" ? null : Number(i.costo_unitario),
        precio_unitario: i.precio_unitario === "" ? null : Number(i.precio_unitario), iva_tipo: i.iva_tipo, tercero: i.tercero || null,
      })),
    };
    setSaving(true);
    try {
      const r = await fetch(orden ? `${API}/admin/taller/ordenes/${orden.id}` : `${API}/admin/taller/ordenes${qs}`, { method: orden ? "PUT" : "POST", headers: jsonH, body: JSON.stringify(body) });
      if (r.ok) { toast.success(orden ? "OT actualizada" : "OT creada"); onSaved(); }
      else { const e = await r.json().catch(() => ({})); toast.error(typeof e.detail === "string" ? e.detail : "No se pudo guardar"); }
    } catch { toast.error("Error de conexión"); }
    setSaving(false);
  };

  return (
    <Modal wide onClose={onClose} title={`${orden ? orden.numero : "Nueva OT"} — ${esCliente ? "Para cliente" : "Uso interno"}${orden ? ` · ${ESTADO_LABEL[orden.estado]}` : ""}`}>
      {!editable && <div className="mb-4 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">Esta OT está {ESTADO_LABEL[orden.estado].toLowerCase()} y no se puede editar.{orden.motivo_anulacion ? ` Motivo: ${orden.motivo_anulacion}` : ""}</div>}
      <fieldset disabled={!editable} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className={LABEL}>Fecha</label><DateInput value={f.fecha} onChange={v => set("fecha", v)} className={INPUT} /></div>
          {esCliente ? (
            <div className="md:col-span-2"><label className={LABEL}>Cliente *</label>
              <EntidadSelect entidades={clientes} value={f.cliente_id} placeholder="Buscar cliente…" onSelect={id => setF(p => ({ ...p, cliente_id: id || "", activo_id: "" }))} />
            </div>
          ) : (
            <div className="md:col-span-2"><label className={LABEL}>Centro de costo</label>
              <select className={SELECT} value={f.centro_costo_id} onChange={e => set("centro_costo_id", e.target.value)} style={{ colorScheme: "dark" }}>
                <option value="">Sin centro de costo</option>
                {centros.filter(c => c.activo !== false || c.id === f.centro_costo_id).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          <div className="md:col-span-2"><label className={LABEL}>Bien (vehículo) *</label>
            <select className={SELECT} value={f.activo_id} onChange={e => set("activo_id", e.target.value)} style={{ colorScheme: "dark" }}>
              <option value="">{esCliente && !f.cliente_id ? "Elegí primero el cliente" : bienes.length ? "Seleccionar bien…" : "No hay bienes (revisá categorías en Configuración)"}</option>
              {orden && !bien && <option value={orden.activo_id}>{orden.activo_nombre}</option>}
              {bienes.map(b => <option key={b.id} value={b.id}>{b.nombre}{b.chasis ? ` · ${b.chasis}` : ""}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={LABEL}>Km{bien?.ultimo_km ? ` (últ. ${num(bien.ultimo_km)})` : ""}</label><input type="number" min="0" className={INPUT} value={f.km} onChange={e => set("km", e.target.value)} /></div>
            <div><label className={LABEL}>Horómetro</label><input type="number" min="0" className={INPUT} value={f.horometro} onChange={e => set("horometro", e.target.value)} /></div>
          </div>
          <div><label className={LABEL}>Mecánico responsable</label><input className={INPUT} value={f.mecanico} onChange={e => set("mecanico", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={LABEL}>Trabajo / falla reportada</label><input className={INPUT} value={f.descripcion} onChange={e => set("descripcion", e.target.value)} /></div>
        </div>

        <div className="border border-white/10 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h4 className="text-sm font-semibold">Repuestos y servicios</h4>
            {editable && <button type="button" className={BTN_SEC} onClick={() => setItems(l => [...l, itemVacio("servicio")])}><Plus className="w-4 h-4" /> Servicio / mano de obra</button>}
          </div>
          {editable && <div className="mb-3"><RepuestoPicker token={token} slug={slug} onPick={addRepuesto} /></div>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 uppercase">
                <tr><th className="text-left px-2 py-1">Descripción</th><th className="px-2 py-1 w-20">Cant.</th>
                  {(!esCliente || verCostos) && <th className="px-2 py-1 w-32">Costo unit.</th>}
                  {esCliente && <th className="px-2 py-1 w-32">Precio venta</th>}
                  <th className="px-2 py-1 w-36">A quién se paga</th><th className="w-8"></th></tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-500 text-xs">Sin ítems</td></tr>}
                {items.map((i, idx) => (
                  <tr key={i.id || idx} className="border-t border-white/5">
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${i.tipo === "repuesto" ? "border-sky-400/30 text-sky-300" : "border-orange-400/30 text-orange-300"}`}>{i.tipo === "repuesto" ? "Rep." : "Serv."}</span>
                        {i.producto_id ? <span className="truncate">{i.descripcion}{i.sku ? <span className="text-slate-500"> · {i.sku}</span> : null}</span>
                          : <input className={INPUT} placeholder="Descripción" value={i.descripcion} onChange={e => setItem(idx, { descripcion: e.target.value })} />}
                      </div>
                    </td>
                    <td className="px-2 py-1"><input type="number" min="0" step="any" className={`${INPUT} text-right`} value={i.cantidad} onChange={e => setItem(idx, { cantidad: e.target.value })} /></td>
                    {(!esCliente || verCostos) && <td className="px-2 py-1"><input type="number" min="0" step="any" className={`${INPUT} text-right`} value={i.costo_unitario ?? ""} onChange={e => setItem(idx, { costo_unitario: e.target.value })} /></td>}
                    {esCliente && <td className="px-2 py-1"><input type="number" min="0" step="any" className={`${INPUT} text-right`} value={i.precio_unitario ?? ""} onChange={e => setItem(idx, { precio_unitario: e.target.value })} /></td>}
                    <td className="px-2 py-1">{i.tipo === "servicio" ? <input className={INPUT} placeholder="Opcional" value={i.tercero} onChange={e => setItem(idx, { tercero: e.target.value })} /> : null}</td>
                    <td className="px-1">{editable && <button type="button" onClick={() => setItems(l => l.filter((_, k) => k !== idx))} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-6 mt-3 text-sm">
            {(!esCliente || verCostos) && <div className="text-slate-400">Costo total: <b className="text-white">{fmt(totalCosto)}</b></div>}
            {esCliente && <div className="text-slate-400">Total a facturar: <b className="text-white">{fmt(totalVenta)}</b></div>}
            {esCliente && verCostos && <div className="text-slate-400">Margen: <b className={totalVenta - totalCosto >= 0 ? "text-emerald-400" : "text-red-400"}>{fmt(totalVenta - totalCosto)}</b></div>}
          </div>
          {cfg?.momento_stock === "cerrar"
            ? <p className="text-[11px] text-slate-500 mt-2">El stock de los repuestos se descuenta al cerrar la OT.</p>
            : <p className="text-[11px] text-slate-500 mt-2">El stock de los repuestos se descuenta al guardar la OT.</p>}
        </div>

        {reglas.length > 0 && (
          <div className="border border-white/10 rounded-xl p-3">
            <h4 className="text-sm font-semibold mb-2">Mantenimientos preventivos que cumple esta OT</h4>
            <div className="flex flex-wrap gap-3">
              {reglas.map(r => (
                <label key={r.id} className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={f.reglas_cumplidas.includes(r.id)} onChange={() => set("reglas_cumplidas", f.reglas_cumplidas.includes(r.id) ? f.reglas_cumplidas.filter(x => x !== r.id) : [...f.reglas_cumplidas, r.id])} />
                  {r.nombre}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Al cerrar la OT se actualiza el km y la fecha del último servicio de las reglas marcadas (se necesita cargar el km).</p>
          </div>
        )}
        <div><label className={LABEL}>Observaciones</label><textarea rows={2} className={INPUT} value={f.observaciones} onChange={e => set("observaciones", e.target.value)} /></div>
      </fieldset>
      <div className="flex justify-end gap-2 mt-5">
        <button className={BTN_SEC} onClick={onClose}>{editable ? "Cancelar" : "Cerrar"}</button>
        {editable && can(orden ? "editar" : "crear") && <button className={BTN_PRI} disabled={saving} onClick={guardar}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>}
      </div>
    </Modal>
  );
}

// ── Bienes / historial ────────────────────────────────────────────────────────
function BienesTab({ API, headers, qs, setHistorial }) {
  const [bienes, setBienes] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    fetch(`${API}/admin/taller/bienes${qs}`, { headers }).then(r => r.ok ? r.json() : []).then(setBienes).catch(() => setBienes([]));
  }, [qs]); // eslint-disable-line
  const abrir = async (b) => {
    const r = await fetch(`${API}/admin/taller/bienes/${b.id}/historial`, { headers });
    if (r.ok) setHistorial(await r.json()); else toast.error("No se pudo cargar el historial");
  };
  const lista = bienes.filter(b => !q.trim() || `${b.nombre} ${b.chasis || ""} ${b.marca || ""} ${b.modelo || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="relative w-64 mb-4"><Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" /><input className={`${INPUT} pl-8`} placeholder="Buscar bien…" value={q} onChange={e => setQ(e.target.value)} /></div>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase"><tr><th className="text-left px-4 py-2">Bien</th><th className="text-left px-4 py-2">Categoría</th><th className="text-left px-4 py-2">Marca / Modelo</th><th className="text-right px-4 py-2">Último km</th><th></th></tr></thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No hay bienes. Definí qué categorías del inventario técnico son vehículos en Configuración.</td></tr>}
            {lista.map(b => (
              <tr key={b.id} className="border-t border-white/5">
                <td className="px-4 py-2">{b.nombre}</td><td className="px-4 py-2 text-slate-300">{b.categoria}</td>
                <td className="px-4 py-2 text-slate-300">{[b.marca, b.modelo].filter(Boolean).join(" ") || "—"}</td>
                <td className="px-4 py-2 text-right">{num(b.ultimo_km)}</td>
                <td className="px-4 py-2 text-right"><button className={BTN_SEC} onClick={() => abrir(b)}><History className="w-4 h-4" /> Historial</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HistorialModal({ data, onClose }) {
  return (
    <Modal wide title={`Historial — ${data.bien.nombre}`} onClose={onClose}>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white/5 rounded-lg p-3"><div className="text-xs text-slate-400">OT realizadas</div><div className="text-lg font-semibold">{data.cantidad_ot}</div></div>
        <div className="bg-white/5 rounded-lg p-3"><div className="text-xs text-slate-400">Último km</div><div className="text-lg font-semibold">{num(data.ultimo_km)}</div></div>
        <div className="bg-white/5 rounded-lg p-3"><div className="text-xs text-slate-400">Costo acumulado</div><div className="text-lg font-semibold">{fmt(data.costo_acumulado)}</div></div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs text-slate-400 uppercase"><tr><th className="text-left px-2 py-1">OT</th><th className="text-left px-2 py-1">Fecha</th><th className="text-right px-2 py-1">Km</th><th className="text-left px-2 py-1">Trabajo</th><th className="text-right px-2 py-1">Costo</th><th className="text-left px-2 py-1">Estado</th></tr></thead>
        <tbody>
          {data.ordenes.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-500">Sin OT para este bien</td></tr>}
          {data.ordenes.map(o => (
            <tr key={o.id} className="border-t border-white/5 align-top">
              <td className="px-2 py-1.5 font-mono text-xs">{o.numero}</td><td className="px-2 py-1.5">{o.fecha}</td><td className="px-2 py-1.5 text-right">{num(o.km)}</td>
              <td className="px-2 py-1.5"><div>{o.descripcion || "—"}</div><div className="text-xs text-slate-500">{(o.items || []).map(i => `${i.descripcion} x${num(i.cantidad)}`).join(", ")}</div></td>
              <td className="px-2 py-1.5 text-right">{fmt(o.total_costo)}</td>
              <td className="px-2 py-1.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_COLOR[o.estado]}`}>{ESTADO_LABEL[o.estado]}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}

// ── Preventivo ────────────────────────────────────────────────────────────────
function PreventivoTab({ API, headers, jsonH, qs, can, alertas, fetchAll }) {
  const [reglas, setReglas] = useState([]);
  const [bienes, setBienes] = useState([]);
  const [form, setForm] = useState(null);
  const cargar = useCallback(async () => {
    const [a, b] = await Promise.all([fetch(`${API}/admin/taller/reglas${qs}`, { headers }), fetch(`${API}/admin/taller/bienes${qs}`, { headers })]);
    if (a.ok) setReglas(await a.json());
    if (b.ok) setBienes(await b.json());
  }, [qs]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);
  const guardar = async () => {
    const body = { ...form, cada_km: form.cada_km === "" ? null : Number(form.cada_km), cada_dias: form.cada_dias === "" ? null : Number(form.cada_dias), ultimo_km: form.ultimo_km === "" ? null : Number(form.ultimo_km), ultima_fecha: form.ultima_fecha || null };
    const r = await fetch(form.id ? `${API}/admin/taller/reglas/${form.id}` : `${API}/admin/taller/reglas${qs}`, { method: form.id ? "PUT" : "POST", headers: jsonH, body: JSON.stringify(body) });
    if (r.ok) { toast.success("Regla guardada"); setForm(null); cargar(); fetchAll(); }
    else { const e = await r.json().catch(() => ({})); toast.error(typeof e.detail === "string" ? e.detail : "No se pudo guardar"); }
  };
  const borrar = async (r) => {
    if (!window.confirm(`¿Eliminar la regla "${r.nombre}"?`)) return;
    await fetch(`${API}/admin/taller/reglas/${r.id}`, { method: "DELETE", headers });
    cargar(); fetchAll();
  };
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold mb-2">Alertas</h3>
        {alertas.length === 0 ? <p className="text-sm text-slate-500">Sin mantenimientos vencidos ni próximos.</p> : (
          <div className="space-y-2">
            {alertas.map(a => (
              <div key={a.id} className={`flex items-center gap-3 rounded-lg border px-4 py-2 text-sm ${a.estado_alerta === "vencida" ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`}>
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="flex-1"><b>{a.activo_nombre}</b> — {a.nombre} <span className="opacity-80">({a.detalle})</span></span>
                <span className="text-xs uppercase">{a.estado_alerta === "vencida" ? "Vencida" : "Próxima"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Reglas de mantenimiento</h3>
          {can("preventivo") && <button className={BTN_PRI} onClick={() => setForm({ activo_id: "", nombre: "", cada_km: "", cada_dias: "", ultimo_km: "", ultima_fecha: "", activa: true })}><Plus className="w-4 h-4" /> Nueva regla</button>}
        </div>
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase"><tr><th className="text-left px-4 py-2">Bien</th><th className="text-left px-4 py-2">Servicio</th><th className="text-right px-4 py-2">Cada km</th><th className="text-right px-4 py-2">Cada días</th><th className="text-right px-4 py-2">Último km</th><th className="text-left px-4 py-2">Última fecha</th><th></th></tr></thead>
            <tbody>
              {reglas.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Sin reglas</td></tr>}
              {reglas.map(r => (
                <tr key={r.id} className="border-t border-white/5">
                  <td className="px-4 py-2">{r.activo_nombre}</td><td className="px-4 py-2">{r.nombre}</td>
                  <td className="px-4 py-2 text-right">{num(r.cada_km)}</td><td className="px-4 py-2 text-right">{num(r.cada_dias)}</td>
                  <td className="px-4 py-2 text-right">{num(r.ultimo_km)}</td><td className="px-4 py-2">{r.ultima_fecha || "—"}</td>
                  <td className="px-4 py-2 text-right">{can("preventivo") && <><button className="p-1.5 text-slate-400 hover:text-white" onClick={() => setForm({ ...r, cada_km: r.cada_km ?? "", cada_dias: r.cada_dias ?? "", ultimo_km: r.ultimo_km ?? "", ultima_fecha: r.ultima_fecha || "" })}><Pencil className="w-4 h-4" /></button><button className="p-1.5 text-slate-500 hover:text-red-400" onClick={() => borrar(r)}><Trash2 className="w-4 h-4" /></button></>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {form && (
        <Modal title={form.id ? "Editar regla" : "Nueva regla de mantenimiento"} onClose={() => setForm(null)}>
          <div className="space-y-3">
            <div><label className={LABEL}>Bien *</label>
              <select className={SELECT} disabled={!!form.id} value={form.activo_id} onChange={e => setForm(f => ({ ...f, activo_id: e.target.value }))} style={{ colorScheme: "dark" }}>
                <option value="">Seleccionar…</option>{bienes.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select></div>
            <div><label className={LABEL}>Servicio (ej. Cambio de aceite) *</label><input className={INPUT} value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={LABEL}>Cada (km)</label><input type="number" min="0" className={INPUT} value={form.cada_km} onChange={e => setForm(f => ({ ...f, cada_km: e.target.value }))} /></div>
              <div><label className={LABEL}>Cada (días)</label><input type="number" min="0" className={INPUT} value={form.cada_dias} onChange={e => setForm(f => ({ ...f, cada_dias: e.target.value }))} /></div>
              <div><label className={LABEL}>Último servicio: km</label><input type="number" min="0" className={INPUT} value={form.ultimo_km} onChange={e => setForm(f => ({ ...f, ultimo_km: e.target.value }))} /></div>
              <div><label className={LABEL}>Último servicio: fecha</label><DateInput value={form.ultima_fecha} onChange={v => setForm(f => ({ ...f, ultima_fecha: v }))} className={INPUT} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.activa !== false} onChange={e => setForm(f => ({ ...f, activa: e.target.checked }))} /> Regla activa</label>
            <div className="flex justify-end gap-2"><button className={BTN_SEC} onClick={() => setForm(null)}>Cancelar</button><button className={BTN_PRI} onClick={guardar}><Save className="w-4 h-4" /> Guardar</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Reportes ──────────────────────────────────────────────────────────────────
function ReportesTab({ API, headers, qs, tipos }) {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [tipo, setTipo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const cargar = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (desde) p.set("desde", desde); if (hasta) p.set("hasta", hasta); if (tipo) p.set("tipo", tipo);
    const r = await fetch(`${API}/admin/taller/reportes${qs}${qs ? "&" : "?"}${p}`, { headers });
    if (r.ok) setData(await r.json()); else toast.error("No se pudo generar el reporte");
    setLoading(false);
  };
  useEffect(() => { cargar(); }, []); // eslint-disable-line
  const Tabla = ({ titulo, cols, rows }) => (
    <div className="mb-6">
      <h3 className="text-sm font-semibold mb-2">{titulo}</h3>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase"><tr>{cols.map(c => <th key={c.k} className={`px-4 py-2 ${c.r ? "text-right" : "text-left"}`}>{c.t}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-4 text-center text-slate-500">Sin datos</td></tr>}
            {rows.map((r, i) => <tr key={i} className="border-t border-white/5">{cols.map(c => <td key={c.k} className={`px-4 py-2 ${c.r ? "text-right" : ""}`}>{c.f ? c.f(r[c.k]) : (r[c.k] ?? "—")}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
  return (
    <div>
      <div className="flex items-end gap-3 mb-5 flex-wrap">
        <div><label className={LABEL}>Desde</label><DateInput value={desde} onChange={setDesde} className={INPUT} /></div>
        <div><label className={LABEL}>Hasta</label><DateInput value={hasta} onChange={setHasta} className={INPUT} /></div>
        {tipos.length > 1 && <div><label className={LABEL}>Tipo</label><select className={SELECT} value={tipo} onChange={e => setTipo(e.target.value)} style={{ colorScheme: "dark" }}><option value="">Todos</option><option value="interna">Uso interno</option><option value="cliente">Clientes</option></select></div>}
        <button className={BTN_PRI} onClick={cargar} disabled={loading}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Generar</button>
        {data && <span className="text-xs text-slate-400 pb-2">{data.cantidad_ot} OT cerradas/facturadas</span>}
      </div>
      {data && (
        <>
          <Tabla titulo="Costo por bien" rows={data.por_bien} cols={[{ k: "bien", t: "Bien" }, { k: "ot", t: "OT", r: 1 }, { k: "ultimo_km", t: "Último km", r: 1, f: num }, { k: "costo", t: "Costo", r: 1, f: fmt }, { k: "venta", t: "Facturado", r: 1, f: fmt }]} />
          {data.por_centro.length > 0 && <Tabla titulo="Costo por centro de costo (uso interno)" rows={data.por_centro} cols={[{ k: "centro", t: "Centro de costo" }, { k: "ot", t: "OT", r: 1 }, { k: "costo", t: "Costo", r: 1, f: fmt }]} />}
          {data.por_cliente.length > 0 && <Tabla titulo="Clientes: venta y margen" rows={data.por_cliente} cols={[{ k: "cliente", t: "Cliente" }, { k: "ot", t: "OT", r: 1 }, { k: "venta", t: "Venta", r: 1, f: fmt }, { k: "costo", t: "Costo", r: 1, f: fmt }, { k: "margen", t: "Margen", r: 1, f: fmt }]} />}
          <Tabla titulo="Repuestos consumidos" rows={data.repuestos} cols={[{ k: "descripcion", t: "Repuesto" }, { k: "sku", t: "SKU" }, { k: "cantidad", t: "Cantidad", r: 1, f: num }, { k: "costo", t: "Costo", r: 1, f: fmt }]} />
        </>
      )}
    </div>
  );
}

// ── Configuración ─────────────────────────────────────────────────────────────
function ConfigTab({ API, headers, jsonH, qs, slug, cfg, centros, onSaved }) {
  const [modo, setModo] = useState(cfg?.modo || "ambos");
  const [momento, setMomento] = useState(cfg?.momento_stock || "cerrar");
  const [depId, setDepId] = useState(cfg?.deposito_id || "");
  const [cats, setCats] = useState(cfg?.categorias_vehiculo || []);
  const [catsDisp, setCatsDisp] = useState([]);
  const [deps, setDeps] = useState([]);
  const [nuevaCat, setNuevaCat] = useState("");
  const [nuevoCentro, setNuevoCentro] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    fetch(`${API}/admin/taller/categorias-inventario${qs}`, { headers }).then(r => r.ok ? r.json() : []).then(setCatsDisp).catch(() => {});
    fetch(`${API}/admin/depositos${qs}`, { headers }).then(r => r.ok ? r.json() : []).then(d => setDeps(Array.isArray(d) ? d : (d.depositos || []))).catch(() => {});
  }, [qs]); // eslint-disable-line
  const todas = Array.from(new Set([...catsDisp, ...cats]));
  const guardar = async () => {
    setSaving(true);
    const r = await fetch(`${API}/admin/taller/config${qs}`, { method: "PUT", headers: jsonH, body: JSON.stringify({ modo, categorias_vehiculo: cats, deposito_id: depId || null, momento_stock: momento }) });
    if (r.ok) { toast.success("Configuración guardada"); onSaved(); } else toast.error("No se pudo guardar");
    setSaving(false);
  };
  const addCentro = async () => {
    if (!nuevoCentro.trim()) return;
    const r = await fetch(`${API}/admin/taller/centros-costo${qs}`, { method: "POST", headers: jsonH, body: JSON.stringify({ nombre: nuevoCentro }) });
    if (r.ok) { setNuevoCentro(""); onSaved(); } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "No se pudo crear"); }
  };
  const delCentro = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.nombre}"?`)) return;
    const r = await fetch(`${API}/admin/taller/centros-costo/${c.id}`, { method: "DELETE", headers });
    if (r.ok) { const d = await r.json(); if (d.inactivado) toast.info("Tenía OT asociadas: quedó inactivo"); onSaved(); }
  };
  return (
    <div className="max-w-3xl space-y-6">
      <section className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
        <h3 className="text-sm font-semibold mb-1">Modo de uso</h3>
        <p className="text-xs text-slate-500 mb-3">Define qué se muestra en el módulo.</p>
        <div className="flex gap-4 flex-wrap">
          {[["interno", "Solo uso interno"], ["clientes", "Solo clientes"], ["ambos", "Ambos"]].map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-sm text-slate-300"><input type="radio" name="modo" checked={modo === v} onChange={() => setModo(v)} /> {l}</label>
          ))}
        </div>
      </section>
      <section className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
        <h3 className="text-sm font-semibold mb-1">Categorías del inventario técnico que son vehículos</h3>
        <p className="text-xs text-slate-500 mb-3">Los activos de estas categorías aparecen como "bienes" al crear una OT.</p>
        <div className="flex flex-wrap gap-3 mb-3">
          {todas.length === 0 && <span className="text-xs text-slate-500">No hay categorías en el inventario técnico todavía.</span>}
          {todas.map(c => (
            <label key={c} className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={cats.includes(c)} onChange={() => setCats(l => l.includes(c) ? l.filter(x => x !== c) : [...l, c])} /> {c}</label>
          ))}
        </div>
        <div className="flex gap-2"><input className={`${INPUT} max-w-xs`} placeholder="Otra categoría (nombre exacto)" value={nuevaCat} onChange={e => setNuevaCat(e.target.value)} />
          <button className={BTN_SEC} onClick={() => { if (nuevaCat.trim()) { setCats(l => Array.from(new Set([...l, nuevaCat.trim()]))); setNuevaCat(""); } }}><Plus className="w-4 h-4" /> Agregar</button></div>
      </section>
      <section className="bg-white/[0.03] border border-white/10 rounded-xl p-4 space-y-3">
        <h3 className="text-sm font-semibold">Stock de repuestos</h3>
        <div><label className={LABEL}>Descontar el stock…</label>
          <select className={`${SELECT} max-w-xs`} value={momento} onChange={e => setMomento(e.target.value)} style={{ colorScheme: "dark" }}>
            <option value="cerrar">Al cerrar la OT</option><option value="agregar">Al guardar la OT (cuando se agrega el repuesto)</option>
          </select></div>
        {deps.length > 0 && (
          <div><label className={LABEL}>Depósito por defecto de los repuestos</label>
            <select className={`${SELECT} max-w-xs`} value={depId} onChange={e => setDepId(e.target.value)} style={{ colorScheme: "dark" }}>
              <option value="">Depósito principal</option>{deps.map(d => <option key={d.id} value={d.id}>{d.codigo ? `${d.codigo} · ` : ""}{d.nombre}</option>)}
            </select></div>
        )}
      </section>
      <button className={BTN_PRI} onClick={guardar} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar configuración</button>
      {(modo === "interno" || modo === "ambos") && (
        <section className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Centros de costo (uso interno)</h3>
          <div className="flex gap-2 mb-3"><input className={`${INPUT} max-w-xs`} placeholder="Ej. Flota, Obra, Administración" value={nuevoCentro} onChange={e => setNuevoCentro(e.target.value)} onKeyDown={e => e.key === "Enter" && addCentro()} />
            <button className={BTN_SEC} onClick={addCentro}><Plus className="w-4 h-4" /> Agregar</button></div>
          <div className="space-y-1">
            {centros.length === 0 && <p className="text-xs text-slate-500">Sin centros de costo</p>}
            {centros.map(c => (
              <div key={c.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-1.5 text-sm">
                <span className={c.activo === false ? "text-slate-500 line-through" : ""}>{c.nombre}</span>
                <button onClick={() => delCentro(c)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
